import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React from 'react'

import { UnstyledButton } from '../../components/UnstyledButton'
import { joinOptionalStringsToSpaceSeparatedString } from '../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars } from '../themes'
import { Accordion, AccordionPhase } from './Accordion'
import type { Icon16 } from './FoundationIcon/icons'
import { IconButton } from './IconButton'
import { Popover } from './Popover'
import {
  ROW_MARGIN_BORDERED,
  ROW_TRANSITION_DURATION,
  ROW_TRANSITION_TIMING_FUNCTION,
} from './Rows/Constants'
import {
  type AnnotationProps,
  RowUtils,
  type StartElemIconProps,
  type StartElemNoneProps,
  type StartElemNumberPogProps,
  type StartElemPogProps,
} from './Rows/RowUtils'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const styles = {
  headerSection: css({
    cursor: 'pointer',
    // TODO: clean up absolute pixel values throughout rows to
    // some centralized constants (investigate if CssVars.size)
    // is appropriate.
    margin: `0 calc(-2 * ${CssVars.size.gutterWidth})`,
    padding: `0 calc(2 * ${CssVars.size.gutterWidth})`,
  }),
  mainTextContainer: css({
    display: 'flex',
  }),
  trailingTextWrapper: css({
    textAlign: 'end',
  }),
}

type Props = (
  | StartElemIconProps
  | StartElemPogProps
  | StartElemNumberPogProps
  | StartElemNoneProps
) &
  AnnotationProps & {
    /**
     * Main leading text.
     */
    label: string
    /**
     * Determines if content is visible.
     */
    isExpanded: boolean
    /**
     * Runs when a toggle to isExpanded is triggered.
     */
    onToggleExpand: (newIsExpanded?: boolean) => void
    /**
     * Secondary leading text.
     */
    caption?: string
    /**
     * Trailing main text.
     */
    value?: string
    /**
     * Trailing secondary text.
     */
    metadata?: string
    /**
     * Optional icon in trailing content.
     */
    endIcon?: Icon16
    /**
     * The content responsible for allowing the user to update
     * settings.
     *
     * Can be a render prop that accepts a ref for element to
     * be focused on when opened.
     */
    children?:
      | React.ReactNode
      | ((options: {
          autoFocusRef: React.Ref<HTMLInputElement>
        }) => React.ReactNode)
    overrideRootStyles?: CSSInterpolation
    'aria-describedby'?: React.HTMLAttributes<HTMLDivElement>['aria-describedby']
    'aria-labelledby'?: React.HTMLAttributes<HTMLDivElement>['aria-labelledby']
    'aria-label'?: React.HTMLAttributes<HTMLDivElement>['aria-label']
  } & (
    | {
        /**
         * `section` gives the row a visual container when expanded.
         * It also adds hover/clicked background colors to the row
         * when not expanded.
         *
         * `accordion` makes the row borderless when expanded, and
         * doesn't change background-color when hovering/clicking.
         *
         * Ask your designer which one is right for a particular use case.
         */
        variant: 'section'
        /**
         * Disables interactions with the expandable row. Only for
         * type === 'section'.
         */
        isDisabled?: boolean
      }
    | {
        variant?: 'accordion'
        isDisabled?: never
      }
  )
/**
 * Expandable Rows are a row object that can be tapped to
 * expand and reveal additional content of any type.
 */
export function ExpandableRow(props: Props) {
  const {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    caption,
    children,
    endIcon,
    isExpanded,
    label,
    metadata,
    onToggleExpand,
    overrideRootStyles,
    value,
    ...startElemProps
  } = props
  const variant = props.variant || 'accordion'
  const isDisabled = props.variant === 'section' && props.isDisabled

  // NOTE(omer.demirkan): isTransitioning is necessary to avoid
  // hover/clicked styles on container when the section is closing.
  const [isTransitioning, setIsTransitioning] = React.useState(false)

  const { colors } = RowUtils.useRowColors(isDisabled)

  const isMountedRef = React.useRef(false)
  // NOTE(omer.demirkan): useLayoutEffect to fix split-second
  // hover style on isExpanded true -> false bug. This may be caused
  // by the Accordion using a setTimeout(..., 50) to ensure a
  // render with pixel values before setting 0px.
  React.useLayoutEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    setIsTransitioning(true)
  }, [isExpanded])
  const handleAccordionStateChange = React.useCallback(
    (_: any, phase: AccordionPhase) => {
      setIsTransitioning(phase === AccordionPhase.Animate)
    },
    []
  )

  const autoFocusRef = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (!isExpanded && !isTransitioning) autoFocusRef.current?.focus()
  }, [isExpanded, isTransitioning])

  const annotationIconRef = React.useRef<HTMLButtonElement>(null)
  const [isAnnotationPopoverOpen, setIsAnnotationPopoverOpen] =
    React.useState(false)
  // NOTE(omer.demirkan): To disable hover/clicked styles on container
  // when hovering over annotation icon.
  const [isHoveringOnAnnotationIcon, setIsHoveringOnAnnotationIcon] =
    React.useState(false)
  const handleAnnotationIconMouseEnter = React.useCallback(() => {
    setIsHoveringOnAnnotationIcon(true)
  }, [])
  const handleAnnotationIconMouseLeave = React.useCallback(() => {
    setIsHoveringOnAnnotationIcon(false)
  }, [])
  const handleAnnotationIconClick = React.useCallback(() => {
    setIsAnnotationPopoverOpen(prev => !prev)
  }, [])
  const handleAnnotationPopoverClose = React.useCallback(() => {
    setIsAnnotationPopoverOpen(false)
  }, [])

  const handleToggleExpandedClicked = React.useCallback(
    (event: React.MouseEvent) => {
      if (
        isDisabled ||
        isAnnotationPopoverOpen ||
        annotationIconRef.current?.contains(event.target as Node)
      )
        return

      onToggleExpand(!isExpanded)
      // NOTE(omer.demirkan): This is passed into both the header and the
      // caret button. stopPropagation is to avoid this being called
      // twice from one click on the caret icon.
      // We're not just omiting this from the toggle button since a
      // type="button" without an onclick isn't a pattern we want to
      // get used to.
      event.stopPropagation()
    },
    [isDisabled, onToggleExpand, isExpanded, isAnnotationPopoverOpen]
  )

  const instanceId = useUniqueId()
  const annotationPopoverId = `expandable-row-${instanceId}-annotation-popover`
  const labelId = `expandable-row-${instanceId}-label`
  const captionId = `expandable-row-${instanceId}-caption`
  const valueId = `expandable-row-${instanceId}-value`
  const metadataId = `expandable-row-${instanceId}-metadata`
  const descriptiveIds = joinOptionalStringsToSpaceSeparatedString(
    ariaDescribedBy,
    labelId,
    caption && captionId,
    value && valueId,
    metadata && metadataId
  )
  const expandableSectionId = useUniqueId()

  const isClosing = !isExpanded && isTransitioning

  return (
    <div
      aria-describedby={descriptiveIds}
      aria-disabled={isDisabled}
      aria-label={ariaLabel || `Information on ${label}`}
      aria-labelledby={ariaLabelledBy}
      css={[
        variant === 'section' && {
          '&:first-of-type': {
            marginTop: '0',
          },
          margin: isExpanded ? `${ROW_MARGIN_BORDERED} 0` : '0',
          transition: `margin ${ROW_TRANSITION_DURATION} ${ROW_TRANSITION_TIMING_FUNCTION}`,
        },
        variant === 'accordion' && {
          borderBottom: `1px solid ${CssVars.neutral.bg3}`,
        },
        overrideRootStyles,
      ]}
    >
      <RowUtils.Container
        isDisabled={isDisabled}
        isElevated={variant === 'section' && !isDisabled && isExpanded}
        isInteractive={
          variant === 'section' &&
          !isDisabled &&
          !isExpanded &&
          !isClosing &&
          !isHoveringOnAnnotationIcon &&
          !isAnnotationPopoverOpen
        }
      >
        <div
          css={[
            styles.headerSection,
            variant === 'section' && isDisabled && { cursor: 'auto' },
          ]}
          onClick={handleToggleExpandedClicked}
        >
          <RowUtils.Section
            // NOTE(omer.demirkan): Accordion type expandable row
            // has a divider that moves down with the expanded content,
            // not paired with the header.
            divider={variant === 'section' && 'bottom'}
            leadingContent={<RowUtils.LeadingContent {...startElemProps} />}
            mainContent={
              <>
                <div css={styles.mainTextContainer}>
                  <span id={labelId}>
                    <Type.Text
                      color={colors.primaryText}
                      // NOTE(omer.demirkan): We don't know the heading hierarchy
                      // where this component is used.
                      element="p"
                      maxLines={3}
                      size="M"
                    >
                      {label}
                    </Type.Text>
                  </span>

                  {/* NOTE(omer.demirkan): So as to not require an extra
                  discriminating prop, but still enforce no annotationTitle
                  without annotation. */}
                  {'annotation' in props && (
                    <>
                      <span>
                        <IconButton
                          aria-controls={annotationPopoverId}
                          aria-describedby={annotationPopoverId}
                          aria-expanded={isAnnotationPopoverOpen}
                          // TODO: translate this when react-intl is available
                          aria-label={`More information on ${label}`}
                          aria-owns={annotationPopoverId}
                          foregroundColor={colors.secondaryText}
                          icon="Question16"
                          innerRef={annotationIconRef}
                          onClick={handleAnnotationIconClick}
                          onMouseEnter={handleAnnotationIconMouseEnter}
                          onMouseLeave={handleAnnotationIconMouseLeave}
                          overrideRootStyles={{
                            ':not(:hover)': {
                              backgroundColor: 'transparent',
                            },
                          }}
                          state={isDisabled ? 'disabled' : undefined}
                          type="button"
                        />
                      </span>
                      <Popover
                        id={annotationPopoverId}
                        isOpen={isAnnotationPopoverOpen}
                        onClose={handleAnnotationPopoverClose}
                        targetEl={annotationIconRef.current}
                      >
                        <Popover.Header
                          title={props.annotationTitle || label}
                        />
                        <Popover.Body content={props.annotation} />
                      </Popover>
                    </>
                  )}
                </div>

                {caption && (
                  <span id={captionId}>
                    <Type.Text
                      color={colors.secondaryText}
                      element="p"
                      maxLines={3}
                      size="S"
                    >
                      {caption}
                    </Type.Text>
                  </span>
                )}
              </>
            }
            trailingContent={
              <>
                {(value || metadata || endIcon) && (
                  <div css={RowUtils.contentGroupStyles}>
                    {(value || metadata) && (
                      <div css={styles.trailingTextWrapper}>
                        {value && (
                          <span id={valueId}>
                            <Type.Text
                              color={colors.primaryText}
                              element="h6"
                              maxLines={3}
                              size="M"
                            >
                              {value}
                            </Type.Text>
                          </span>
                        )}
                        {metadata && (
                          <span id={metadataId}>
                            <Type.Text
                              color={colors.secondaryText}
                              element="p"
                              maxLines={3}
                              size="S"
                            >
                              {metadata}
                            </Type.Text>
                          </span>
                        )}
                      </div>
                    )}

                    {endIcon && <RowUtils.Icon type={endIcon} />}
                  </div>
                )}
                <div>
                  {/* NOTE(omer.demirkan): extra div because rotation animation
                  on div with margin/padding is off-center */}
                  <UnstyledButton
                    aria-controls={expandableSectionId}
                    aria-describedby={descriptiveIds}
                    aria-disabled={isDisabled}
                    aria-expanded={isExpanded}
                    aria-label={`${
                      // TODO: translate this when react-intl is available
                      isExpanded ? 'Close' : 'Open'
                    } ${label} details`}
                    aria-owns={expandableSectionId}
                    css={{
                      // NOTE(omer.demirkan): We need display: flex to fix
                      // the padding-bottom issue on FoundationIcons. Wrapping
                      // this in a button seems to re-introduce it otherwise.
                      display: 'flex',
                      transform: `rotate(${isExpanded ? 180 : 0}deg)`,
                      transition: `transform ${ROW_TRANSITION_DURATION} ${ROW_TRANSITION_TIMING_FUNCTION}`,
                    }}
                    onClick={handleToggleExpandedClicked}
                    type="button"
                  >
                    <RowUtils.Icon color={colors.caret} type="CaretDown16" />
                  </UnstyledButton>
                </div>
              </>
            }
          />
        </div>

        <Accordion
          isOpen={isExpanded}
          onStateChange={handleAccordionStateChange}
          overrideRootStyles={{
            transition: `height ${ROW_TRANSITION_DURATION} ${ROW_TRANSITION_TIMING_FUNCTION}`,
          }}
        >
          <div
            // NOTE(omer.demirkan): This is following w3's accordion (with inputs) example.
            // https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
            aria-labelledby={labelId}
            css={{
              paddingBottom: `calc(2 * ${CssVars.size.gutterWidth})`,
              paddingTop:
                variant === 'section'
                  ? `calc(2 * ${CssVars.size.gutterWidth})`
                  : CssVars.size.gutterWidthSm,
            }}
            id={expandableSectionId}
            role="region"
          >
            {typeof children === 'function'
              ? children({ autoFocusRef })
              : children}
          </div>
        </Accordion>
      </RowUtils.Container>
    </div>
  )
}
